/**
 * EaaFilters Component
 *
 * Filter controls for the EAA compliance tab.
 * Allows filtering by severity, category, viewport, and page.
 */

import { useState, useMemo } from 'react';
import {
  Filter,
  X,
  Search,
  AlertTriangle,
  AlertCircle,
  Info,
  Monitor,
  Smartphone,
  Layers,
} from 'lucide-react';
import type { ViewportType } from '../ui/ViewportBadge';

export type Severity = 'critical' | 'serious' | 'moderate' | 'minor';

export type EaaCategory =
  | 'keyboard-navigation'
  | 'screen-reader-support'
  | 'focus-management'
  | 'color-contrast'
  | 'headings-structure'
  | 'aria-semantics'
  | 'links-buttons'
  | 'interactive-elements'
  | 'forms'
  | 'media'
  | 'common-pitfalls'
  | 'skip-links-landmarks'
  | 'mobile-responsive'
  | 'cookies-consent';

export interface EaaFiltersState {
  severities: Severity[];
  categories: EaaCategory[];
  viewports: ViewportType[];
  pages: string[];
  searchQuery: string;
  /** When true, group duplicate issues across pages into single entries */
  deduplicate: boolean;
}

interface EaaFiltersProps {
  filters: EaaFiltersState;
  onFiltersChange: (filters: EaaFiltersState) => void;
  availablePages?: string[];
  issueCount: number;
  filteredCount: number;
}

const SEVERITY_OPTIONS: { value: Severity; label: string; icon: typeof AlertTriangle; color: string }[] = [
  { value: 'critical', label: 'Critical', icon: AlertTriangle, color: 'text-red-600' },
  { value: 'serious', label: 'Serious', icon: AlertCircle, color: 'text-orange-500' },
  { value: 'moderate', label: 'Moderate', icon: Info, color: 'text-yellow-600' },
  { value: 'minor', label: 'Minor', icon: Info, color: 'text-blue-500' },
];

const CATEGORY_OPTIONS: { value: EaaCategory; label: string; shortLabel: string }[] = [
  { value: 'keyboard-navigation', label: 'Keyboard Navigation', shortLabel: 'Keyboard' },
  { value: 'screen-reader-support', label: 'Screen Reader Support', shortLabel: 'Screen Reader' },
  { value: 'focus-management', label: 'Focus Management', shortLabel: 'Focus' },
  { value: 'color-contrast', label: 'Color Contrast', shortLabel: 'Contrast' },
  { value: 'headings-structure', label: 'Headings & Structure', shortLabel: 'Headings' },
  { value: 'aria-semantics', label: 'ARIA & Semantics', shortLabel: 'ARIA' },
  { value: 'links-buttons', label: 'Links & Buttons', shortLabel: 'Links' },
  { value: 'interactive-elements', label: 'Interactive Elements', shortLabel: 'Interactive' },
  { value: 'forms', label: 'Forms', shortLabel: 'Forms' },
  { value: 'media', label: 'Media', shortLabel: 'Media' },
  { value: 'common-pitfalls', label: 'Common Pitfalls', shortLabel: 'Pitfalls' },
  { value: 'skip-links-landmarks', label: 'Skip Links & Landmarks', shortLabel: 'Skip Links' },
  { value: 'mobile-responsive', label: 'Mobile & Responsive', shortLabel: 'Mobile' },
  { value: 'cookies-consent', label: 'Cookies & Consent', shortLabel: 'Cookies' },
];

const VIEWPORT_OPTIONS: { value: ViewportType; label: string; icon: typeof Monitor }[] = [
  { value: 'desktop', label: 'Desktop', icon: Monitor },
  { value: 'mobile', label: 'Mobile', icon: Smartphone },
  { value: 'both', label: 'All Viewports', icon: Layers },
];

export function EaaFilters({
  filters,
  onFiltersChange,
  availablePages = [],
  issueCount,
  filteredCount,
}: EaaFiltersProps) {
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showPageDropdown, setShowPageDropdown] = useState(false);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.severities.length > 0 ||
      filters.categories.length > 0 ||
      filters.viewports.length > 0 ||
      filters.pages.length > 0 ||
      filters.searchQuery.length > 0
    );
  }, [filters]);

  const toggleSeverity = (severity: Severity) => {
    const newSeverities = filters.severities.includes(severity)
      ? filters.severities.filter((s) => s !== severity)
      : [...filters.severities, severity];
    onFiltersChange({ ...filters, severities: newSeverities });
  };

  const toggleCategory = (category: EaaCategory) => {
    const newCategories = filters.categories.includes(category)
      ? filters.categories.filter((c) => c !== category)
      : [...filters.categories, category];
    onFiltersChange({ ...filters, categories: newCategories });
  };

  const toggleViewport = (viewport: ViewportType) => {
    const newViewports = filters.viewports.includes(viewport)
      ? filters.viewports.filter((v) => v !== viewport)
      : [...filters.viewports, viewport];
    onFiltersChange({ ...filters, viewports: newViewports });
  };

  const togglePage = (page: string) => {
    const newPages = filters.pages.includes(page)
      ? filters.pages.filter((p) => p !== page)
      : [...filters.pages, page];
    onFiltersChange({ ...filters, pages: newPages });
  };

  const clearAllFilters = () => {
    onFiltersChange({
      severities: [],
      categories: [],
      viewports: [],
      pages: [],
      searchQuery: '',
      deduplicate: false,
    });
  };

  const toggleDeduplicate = () => {
    onFiltersChange({ ...filters, deduplicate: !filters.deduplicate });
  };

  const removeFilter = (type: 'severity' | 'category' | 'viewport' | 'page', value: string) => {
    switch (type) {
      case 'severity':
        onFiltersChange({
          ...filters,
          severities: filters.severities.filter((s) => s !== value),
        });
        break;
      case 'category':
        onFiltersChange({
          ...filters,
          categories: filters.categories.filter((c) => c !== value),
        });
        break;
      case 'viewport':
        onFiltersChange({
          ...filters,
          viewports: filters.viewports.filter((v) => v !== value),
        });
        break;
      case 'page':
        onFiltersChange({
          ...filters,
          pages: filters.pages.filter((p) => p !== value),
        });
        break;
    }
  };

  return (
    <div className="space-y-4">
      {/* Search and Results Count */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        {/* Search Input */}
        <div className="relative flex-1 max-w-md">
          <Search
            size={18}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-medium"
          />
          <input
            type="text"
            placeholder="Search issues..."
            value={filters.searchQuery}
            onChange={(e) => onFiltersChange({ ...filters, searchQuery: e.target.value })}
            className="w-full pl-10 pr-4 py-2 border border-gray-light rounded-lg text-body-sm focus:outline-none focus:ring-2 focus:ring-coral/50 focus:border-coral"
          />
          {filters.searchQuery && (
            <button
              onClick={() => onFiltersChange({ ...filters, searchQuery: '' })}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-medium hover:text-dark-text"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Results Count */}
        <div className="text-body-sm text-gray-medium">
          Showing{' '}
          <span className="font-semibold text-dark-text">{filteredCount}</span> of{' '}
          <span className="font-semibold text-dark-text">{issueCount}</span> issues
        </div>
      </div>

      {/* Filter Controls */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 text-body-sm text-gray-medium">
          <Filter size={16} />
          <span>Filters:</span>
        </div>

        {/* Severity Filters */}
        <div className="flex gap-1">
          {SEVERITY_OPTIONS.map((option) => {
            const Icon = option.icon;
            const isActive = filters.severities.includes(option.value);
            return (
              <button
                key={option.value}
                onClick={() => toggleSeverity(option.value)}
                className={`flex items-center gap-1 px-2 py-1 rounded text-caption transition-colors ${
                  isActive
                    ? 'bg-coral text-white'
                    : 'bg-gray-light/50 text-gray-medium hover:bg-gray-light'
                }`}
                aria-pressed={isActive}
              >
                <Icon size={12} className={isActive ? '' : option.color} />
                {option.label}
              </button>
            );
          })}
        </div>

        {/* Category Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded text-body-sm transition-colors ${
              filters.categories.length > 0
                ? 'bg-coral text-white'
                : 'bg-gray-light/50 text-gray-medium hover:bg-gray-light'
            }`}
          >
            Categories
            {filters.categories.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-caption">
                {filters.categories.length}
              </span>
            )}
          </button>
          {showCategoryDropdown && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowCategoryDropdown(false)}
              />
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-light rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto min-w-48">
                {CATEGORY_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => toggleCategory(option.value)}
                    className={`w-full text-left px-3 py-2 text-body-sm hover:bg-gray-light/50 flex items-center justify-between ${
                      filters.categories.includes(option.value) ? 'bg-coral/10 text-coral' : ''
                    }`}
                  >
                    <span>{option.label}</span>
                    {filters.categories.includes(option.value) && (
                      <span className="text-coral">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Viewport Filters */}
        <div className="flex gap-1">
          {VIEWPORT_OPTIONS.map((option) => {
            const Icon = option.icon;
            const isActive = filters.viewports.includes(option.value);
            return (
              <button
                key={option.value}
                onClick={() => toggleViewport(option.value)}
                className={`flex items-center gap-1 px-2 py-1 rounded text-caption transition-colors ${
                  isActive
                    ? 'bg-coral text-white'
                    : 'bg-gray-light/50 text-gray-medium hover:bg-gray-light'
                }`}
                aria-pressed={isActive}
              >
                <Icon size={12} />
                {option.label}
              </button>
            );
          })}
        </div>

        {/* Page Filter Dropdown */}
        {availablePages.length > 0 && (
          <div className="relative">
            <button
              onClick={() => setShowPageDropdown(!showPageDropdown)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded text-body-sm transition-colors ${
                filters.pages.length > 0
                  ? 'bg-coral text-white'
                  : 'bg-gray-light/50 text-gray-medium hover:bg-gray-light'
              }`}
            >
              Pages
              {filters.pages.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-caption">
                  {filters.pages.length}
                </span>
              )}
            </button>
            {showPageDropdown && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowPageDropdown(false)}
                />
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-light rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto min-w-64">
                  {availablePages.map((page) => (
                    <button
                      key={page}
                      onClick={() => togglePage(page)}
                      className={`w-full text-left px-3 py-2 text-body-sm hover:bg-gray-light/50 flex items-center justify-between ${
                        filters.pages.includes(page) ? 'bg-coral/10 text-coral' : ''
                      }`}
                    >
                      <span className="truncate">{page}</span>
                      {filters.pages.includes(page) && <span className="text-coral">✓</span>}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Deduplication Toggle */}
        <button
          onClick={toggleDeduplicate}
          className={`flex items-center gap-2 px-3 py-1.5 rounded text-body-sm transition-colors ${
            filters.deduplicate
              ? 'bg-coral text-white'
              : 'bg-gray-light/50 text-gray-medium hover:bg-gray-light'
          }`}
          aria-pressed={filters.deduplicate}
          title={filters.deduplicate ? 'Show all occurrences' : 'Show unique issues'}
        >
          <Layers size={14} />
          {filters.deduplicate ? 'Unique Issues' : 'All Occurrences'}
        </button>

        {/* Clear All */}
        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className="flex items-center gap-1 px-2 py-1 text-caption text-coral hover:text-coral/80 transition-colors"
          >
            <X size={14} />
            Clear all
          </button>
        )}
      </div>

      {/* Active Filter Tags */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2">
          {filters.severities.map((severity) => (
            <span
              key={severity}
              className="inline-flex items-center gap-1 px-2 py-1 bg-coral/10 text-coral rounded text-caption"
            >
              {severity.charAt(0).toUpperCase() + severity.slice(1)}
              <button
                onClick={() => removeFilter('severity', severity)}
                className="hover:text-coral/70"
                aria-label={`Remove ${severity} filter`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
          {filters.categories.map((category) => {
            const option = CATEGORY_OPTIONS.find((c) => c.value === category);
            return (
              <span
                key={category}
                className="inline-flex items-center gap-1 px-2 py-1 bg-coral/10 text-coral rounded text-caption"
              >
                {option?.shortLabel || category}
                <button
                  onClick={() => removeFilter('category', category)}
                  className="hover:text-coral/70"
                  aria-label={`Remove ${category} filter`}
                >
                  <X size={12} />
                </button>
              </span>
            );
          })}
          {filters.viewports.map((viewport) => {
            const option = VIEWPORT_OPTIONS.find((v) => v.value === viewport);
            return (
              <span
                key={viewport}
                className="inline-flex items-center gap-1 px-2 py-1 bg-coral/10 text-coral rounded text-caption"
              >
                {option?.label || viewport}
                <button
                  onClick={() => removeFilter('viewport', viewport)}
                  className="hover:text-coral/70"
                  aria-label={`Remove ${viewport} filter`}
                >
                  <X size={12} />
                </button>
              </span>
            );
          })}
          {filters.pages.map((page) => (
            <span
              key={page}
              className="inline-flex items-center gap-1 px-2 py-1 bg-coral/10 text-coral rounded text-caption max-w-48"
            >
              <span className="truncate">{page}</span>
              <button
                onClick={() => removeFilter('page', page)}
                className="hover:text-coral/70 flex-shrink-0"
                aria-label={`Remove ${page} filter`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default EaaFilters;
